import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  BookOpen,
  Feather,
  Globe2,
  MessageCircle,
  Sparkles,
  Quote,
  ArrowRight,
  ShieldCheck,
  Mail,
  Users,
} from 'lucide-react';
import { useT } from '../context/LanguageContext';

const CATEGORIES = [
  {
    id: 'lire-comprendre',
    title: 'Lire & Comprendre',
    eyebrow: 'Compréhension',
    icon: BookOpen,
    desc: "Développez votre compréhension grâce à des textes progressifs, des activités interactives, du vocabulaire contextualisé et des exercices de réflexion adaptés à chaque niveau.",
  },
  {
    id: 'histoires-progressives',
    title: 'Histoires Progressives',
    eyebrow: 'Niveaux A1 → C2',
    icon: Feather,
    desc: "Découvrez des histoires spécialement conçues pour accompagner votre progression du niveau A1 au niveau C2.",
  },
  {
    id: 'poesie-expression',
    title: 'Poésie & Expression',
    eyebrow: 'Lecture expressive',
    icon: Sparkles,
    desc: 'Explorez la musicalité de la langue française à travers la poésie, la lecture expressive, le rythme, l\'intonation et l\'émotion.',
  },
  {
    id: 'voix-francophones',
    title: 'Voix Francophones',
    eyebrow: 'Cultures du monde',
    icon: Globe2,
    desc: 'Partez à la découverte des cultures, des auteurs et des perspectives du monde francophone : France, Québec, Belgique, Suisse, Afrique francophone et bien d\'autres horizons.',
  },
  {
    id: 'lire-parler',
    title: 'Lire & Parler',
    eyebrow: 'Du texte à la voix',
    icon: MessageCircle,
    desc: 'Transformez chaque lecture en occasion de communiquer : résumer un texte, exprimer une opinion, interpréter un personnage, débattre d\'une idée, développer une réflexion personnelle.',
  },
];

const LEVELS = [
  {
    code: 'A1', name: 'Découverte', tone: 'Premier souffle',
    points: [
      'Lire des phrases simples et de courtes histoires',
      'Comprendre le vocabulaire essentiel',
      'Associer lecture et prononciation',
      'Développer sa confiance',
    ],
  },
  {
    code: 'A2', name: 'Compréhension Quotidienne', tone: 'Le français vivant',
    points: [
      'Lire des dialogues et récits simples',
      'Comprendre des situations de la vie courante',
      'Améliorer sa fluidité',
      'Commencer à exprimer ses opinions',
    ],
  },
  {
    code: 'B1', name: 'Lecture Active', tone: 'Du sens à l\'analyse',
    points: [
      'Lire des textes plus longs',
      'Développer son interprétation',
      'Réagir aux thèmes abordés',
      'Produire des résumés oraux',
    ],
  },
  {
    code: 'B2', name: 'Analyse & Expression', tone: 'Nuance et profondeur',
    points: [
      'Lire des textes authentiques',
      'Comprendre les nuances',
      'Développer son esprit critique',
      'Participer à des discussions approfondies',
    ],
  },
  {
    code: 'C1', name: 'Réflexion Critique', tone: 'Lire entre les lignes',
    points: [
      'Interpréter des textes complexes',
      'Comprendre les implicites',
      'Analyser le style et les idées',
      'Développer une expression sophistiquée',
    ],
  },
  {
    code: 'C2', name: 'Maîtrise & Sensibilité Littéraire', tone: 'L\'art de la langue',
    points: [
      'Explorer la littérature et la pensée francophone',
      'Apprécier les nuances stylistiques',
      'Analyser avec précision',
      'S\'exprimer avec aisance et profondeur',
    ],
  },
];

const ORIGINAL_EXAMPLES = [
  'Histoires originales créées par l\'équipe pédagogique de Selora',
  'Dialogues conçus pour développer la communication réelle',
  'Articles culturels adaptés aux apprenants',
  'Poèmes éducatifs originaux',
  'Activités de lecture exclusives à la plateforme',
];

const AUTHOR_CONTRIBUTIONS = [
  'Poèmes originaux',
  'Nouvelles et récits courts',
  'Articles culturels',
  'Textes francophones',
  'Témoignages et expériences de vie',
  'Activités pédagogiques',
  'Projets collaboratifs',
];

const CONTACT_EMAIL = 'bonjour@selora.app'; // placeholder — replace with the team's real address

export default function LireLeFrancais() {
  const t = useT();
  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* HERO */}
      <section className="pt-14 pb-12 relative overflow-hidden" data-testid="lire-hero">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="flex items-center gap-1.5 text-[10px] text-[#999] mb-6 flex-wrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Selora</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A2F4A] font-bold">Lire le Français</span>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#D4AF37]/50" />
              <BookOpen className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-12 bg-[#D4AF37]/50" />
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2F4A] mb-4 leading-[1.05]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title">
              Lire le Français
            </h1>

            <p
              className="text-base sm:text-lg text-[#6B4E37] italic max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="hero-subtitle">
              {t({
                fr: 'Découvrez le français à travers les histoires, la culture, la poésie et les voix du monde francophone.',
                en: 'Discover French through stories, culture, poetry and the voices of the Francophone world.',
              })}
            </p>
          </div>

          {/* Intro paragraphs */}
          <div className="mt-10 grid gap-5 max-w-2xl mx-auto text-[#1A2F4A]/85 text-[15px] leading-relaxed" data-testid="hero-intro">
            <p>
              {t({
                fr: "Lire une langue, c'est aussi découvrir une culture, une émotion et une manière de penser.",
                en: 'To read a language is also to discover a culture, an emotion and a way of thinking.',
              })}
            </p>
            <p>
              {t({
                fr: "Dans cette section, vous allez explorer le français à travers des histoires, des dialogues, des poèmes, des textes culturels et des œuvres francophones adaptées à chaque niveau. L'objectif n'est pas seulement de comprendre les mots, mais de ressentir la langue, développer votre expression et apprendre à penser en français naturellement.",
                en: 'In this section, you will explore French through stories, dialogues, poems, cultural texts and francophone works adapted to each level. The goal is not only to understand words, but to feel the language, develop your expression and learn to think in French naturally.',
              })}
            </p>
            <p>
              {t({
                fr: "La maîtrise de la langue française ouvre non seulement les portes du monde académique et professionnel, mais aussi celles de la culture, de la pensée et de l'art. À travers la lecture, on découvre différentes façons de voir le monde, d'exprimer les émotions et de comprendre les sociétés francophones dans toute leur richesse et leur diversité.",
                en: 'Mastering the French language opens not only the doors of the academic and professional worlds, but also those of culture, thought and art. Through reading, one discovers different ways of seeing the world, expressing emotions and understanding francophone societies in all their richness and diversity.',
              })}
            </p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHIE */}
      <section className="pb-12" data-testid="philosophie-section">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Engagement éditorial</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="philosophie-title"
            >
              Notre Philosophie
            </h2>
          </div>

          <div className="max-w-2xl mx-auto grid gap-5 text-[#1A2F4A]/85 text-[15px] leading-relaxed">
            <p>
              La plupart des contenus proposés dans <em>Lire le français</em> sont des créations originales conçues
              spécialement pour les apprenants de français.
            </p>
            <p>
              Nos histoires, dialogues, articles, poèmes et activités de lecture sont créés avec un objectif
              pédagogique précis : vous permettre de progresser naturellement en compréhension, en vocabulaire, en
              expression et en confiance.
            </p>
            <p>
              Cette approche nous offre la liberté d'adapter chaque texte au niveau visé, du A1 au C2, tout en
              proposant des situations authentiques, actuelles et proches de la communication réelle.
            </p>
            <p>
              Quelques œuvres appartenant au domaine public pourront également être proposées afin de faire découvrir
              la richesse du patrimoine littéraire francophone. Ces textes seront toujours clairement identifiés.
            </p>
          </div>

          {/* Pledge quote */}
          <figure
            className="max-w-2xl mx-auto mt-8 bg-white border-l-2 border-[#D4AF37] rounded-r-xl px-6 py-5 relative"
            data-testid="philosophie-pledge"
          >
            <Quote className="w-5 h-5 text-[#D4AF37]/70 absolute -top-2.5 left-5 bg-[#FAF7F0] px-0.5" />
            <blockquote
              className="text-[#1A2F4A] text-base leading-relaxed italic"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              « Chaque histoire, article, dialogue, poème et activité est conçu pour aider les apprenants à comprendre,
              réfléchir, s'exprimer et progresser naturellement en français. »
            </blockquote>
            <figcaption className="text-[10px] text-[#6B4E37] uppercase tracking-[0.25em] mt-3 font-bold">
              — Notre engagement
            </figcaption>
          </figure>

          {/* Création Originale Selora badge card */}
          <div
            className="max-w-2xl mx-auto mt-8 bg-gradient-to-br from-[#1A2F4A] to-[#243a58] rounded-2xl px-6 py-6 text-white overflow-hidden relative"
            data-testid="creation-originale-card"
          >
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#D4AF37]/10 rounded-full" aria-hidden="true" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold">Label éditorial</p>
                  <h3
                    className="text-xl font-bold leading-tight flex items-center gap-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    <span className="text-[#D4AF37]" aria-hidden="true">✦</span>
                    Création Originale Selora
                  </h3>
                </div>
              </div>
              <p className="text-[13px] text-white/85 leading-relaxed mb-4">
                Ce badge identifie les contenus spécialement créés pour les apprenants de français par l'équipe
                pédagogique de Selora.
              </p>
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-2">
                Exemples de contenus originaux
              </p>
              <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-5 text-[12px] text-white/85 leading-relaxed">
                {ORIGINAL_EXAMPLES.map((it, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`original-example-${i}`}>
                    <span className="text-[#D4AF37] mt-0.5 flex-shrink-0" aria-hidden="true">✦</span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL FIND HERE */}
      <section className="pb-12" data-testid="what-youll-find-section">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Au programme</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="what-youll-find-title"
            >
              Vous trouverez ici
            </h2>
          </div>

          <div className="max-w-2xl mx-auto bg-white border border-[#E8E4DC] rounded-2xl px-6 py-6" data-testid="hero-overview">
            <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-[13px] text-[#1A2F4A]/90 leading-relaxed">
              {[
                'Des lectures progressives du niveau A1 au C2',
                'Des activités interactives de compréhension',
                'Des textes culturels et littéraires',
                "De la poésie et de la lecture expressive",
                "Des exercices de prononciation et d'oralité",
                "Des questions de réflexion et d'expression orale",
              ].map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                  {it}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold mt-5 pt-4 border-t border-[#F3F4F6] mb-2">
              Après chaque lecture, vous êtes invité(e) à
            </p>
            <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-6 text-[13px] text-[#1A2F4A]/90 leading-relaxed">
              {['parler', 'interpréter', 'donner votre opinion', 'résumer', 'développer votre expression orale'].map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                  {it}
                </li>
              ))}
            </ul>
          </div>

          {/* Closing line */}
          <div className="mt-12 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#D4AF37]/50" />
              <Feather className="w-4 h-4 text-[#D4AF37]" />
              <div className="h-px w-8 bg-[#D4AF37]/50" />
            </div>
            <h2
              className="text-2xl sm:text-3xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="hero-closing"
            >
              Lire le français, c'est vivre le français.
            </h2>
          </div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="pb-16" data-testid="quotes-section">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto" data-testid="hero-quotes">
            {[
              { q: 'Une langue est un monde.', author: 'Maxime française' },
              { q: "Lire, c'est voyager dans une autre voix.", author: 'Selora' },
            ].map((it, i) => (
              <figure
                key={i}
                className="bg-white border border-[#E8E4DC] rounded-2xl px-6 py-5 relative"
                data-testid={`hero-quote-${i}`}
              >
                <Quote className="w-5 h-5 text-[#D4AF37]/70 absolute -top-2.5 left-5 bg-[#FAF7F0] px-0.5" />
                <blockquote
                  className="text-[#1A2F4A] text-base leading-relaxed"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  « {it.q} »
                </blockquote>
                <figcaption className="text-[10px] text-[#6B4E37] uppercase tracking-[0.25em] mt-3 font-bold">
                  — {it.author}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="pb-16 pt-2">
        <div className="max-w-[1000px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Explorer</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="categories-title"
            >
              Cinq portes vers la langue
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-grid">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const isAvailable = c.id === 'lire-comprendre';
              const inner = (
                <div
                  className={`group bg-white border ${isAvailable ? 'border-[#D4AF37]/40 hover:border-[#D4AF37] hover:shadow-md' : 'border-[#E8E4DC] hover:border-[#D4AF37]'} rounded-2xl px-6 py-6 transition-all flex flex-col h-full`}
                  data-testid={`category-${c.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/30 flex items-center justify-center group-hover:bg-[#D4AF37]/10 transition-colors flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-[9px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold">{c.eyebrow}</p>
                      <h3
                        className="text-lg font-bold text-[#1A2F4A] leading-tight"
                        style={{ fontFamily: "'Playfair Display', serif" }}>
                        {c.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-[13px] text-[#6B4E37] leading-relaxed flex-1">{c.desc}</p>
                  <div className={`mt-4 pt-4 border-t border-[#F3F4F6] flex items-center gap-2 text-[11px] ${isAvailable ? 'text-[#1A2F4A] font-semibold group-hover:text-[#D4AF37]' : 'text-[#999] italic'}`}>
                    <span>{isAvailable ? 'Découvrir le niveau A1' : 'Bientôt disponible'}</span>
                    <ArrowRight className="w-3 h-3 text-[#D4AF37]" />
                  </div>
                </div>
              );
              return isAvailable ? (
                <Link key={c.id} to="/lire-le-francais/lire-et-comprendre/a1" className="flex">{inner}</Link>
              ) : (
                <div key={c.id} className="flex">{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEVEL PROGRESSION */}
      <section className="pb-20 pt-4 bg-white border-t border-[#E8E4DC]">
        <div className="max-w-[1000px] mx-auto px-6 pt-12">
          <div className="text-center mb-10">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Progression</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="levels-title"
            >
              De la première phrase à la sensibilité littéraire
            </h2>
            <p className="text-sm text-[#6B4E37] italic max-w-lg mx-auto">
              Un chemin de six étapes — chacune une nouvelle manière d'habiter la langue.
            </p>
          </div>

          <div className="space-y-3" data-testid="levels-list">
            {LEVELS.map((lv, idx) => (
              <div
                key={lv.code}
                className="group bg-[#FAF7F0] hover:bg-white border border-[#E8E4DC] hover:border-[#D4AF37]/50 rounded-2xl px-6 py-5 transition-all"
                data-testid={`level-${lv.code}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="flex sm:flex-col items-center sm:items-start sm:w-32 flex-shrink-0 gap-3 sm:gap-1">
                    <div className="w-12 h-12 rounded-full bg-[#1A2F4A] text-[#D4AF37] flex items-center justify-center font-bold text-sm tracking-wider">
                      {lv.code}
                    </div>
                    <div className="sm:mt-1">
                      <p
                        className="text-base font-bold text-[#1A2F4A] leading-tight"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {lv.name}
                      </p>
                      <p className="text-[10px] text-[#D4AF37] italic uppercase tracking-wider font-bold mt-0.5">
                        {lv.tone}
                      </p>
                    </div>
                  </div>
                  <ul className="flex-1 grid sm:grid-cols-2 gap-y-1.5 gap-x-5 text-[13px] text-[#1A2F4A]/90 leading-relaxed">
                    {lv.points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
                {idx < LEVELS.length - 1 && (
                  <div className="hidden sm:flex items-center gap-2 mt-4 ml-32 opacity-40">
                    <div className="h-px w-12 bg-[#D4AF37]/50" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Final note */}
          <div className="mt-12 text-center max-w-xl mx-auto">
            <p className="text-[15px] text-[#1A2F4A] italic leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
              « Ici, le français ne s'étudie pas seulement — il se vit. »
            </p>
          </div>
        </div>
      </section>

      {/* AUTHORS & CONTRIBUTORS */}
      <section className="pb-24 pt-16 bg-[#FAF7F0] border-t border-[#E8E4DC]" data-testid="authors-section">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">Communauté</p>
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#D4AF37]/50" />
              <Users className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-8 bg-[#D4AF37]/50" />
            </div>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="authors-title"
            >
              Espace Auteurs &amp; Créateurs
            </h2>
          </div>

          <div className="max-w-2xl mx-auto grid gap-5 text-[#1A2F4A]/85 text-[15px] leading-relaxed">
            <p>
              <em>Lire le français</em> a pour mission de faire découvrir la richesse de la langue et des cultures
              francophones à travers des contenus de qualité.
            </p>
            <p>
              Nous privilégions principalement des contenus originaux créés pour l'apprentissage du français.
              Cependant, nous sommes également heureux d'explorer des collaborations avec des auteurs, poètes,
              enseignants, créateurs de contenu et passionnés de la langue française.
            </p>
            <p>
              Si vous souhaitez proposer un texte, un poème, une nouvelle, un article culturel ou tout autre contenu
              susceptible d'enrichir cette plateforme, nous vous invitons à nous contacter. Chaque proposition est
              étudiée avec attention afin de garantir la qualité pédagogique, culturelle et linguistique des contenus
              publiés.
            </p>
          </div>

          {/* Possible contributions */}
          <div className="max-w-2xl mx-auto mt-8 bg-white border border-[#E8E4DC] rounded-2xl px-6 py-5" data-testid="authors-contributions">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-3">
              Exemples de contributions possibles
            </p>
            <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-6 text-[13px] text-[#1A2F4A]/90 leading-relaxed">
              {AUTHOR_CONTRIBUTIONS.map((it, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`author-contribution-${i}`}>
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                  {it}
                </li>
              ))}
            </ul>
          </div>

          {/* Closing statement */}
          <div className="max-w-2xl mx-auto mt-8 text-center">
            <p
              className="text-[17px] text-[#1A2F4A] italic leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="authors-closing"
            >
              « Ensemble, faisons vivre la langue française à travers la lecture, la culture et le partage. »
            </p>
          </div>

          {/* Contact */}
          <div className="max-w-2xl mx-auto mt-8 grid sm:grid-cols-2 gap-3" data-testid="authors-contact">
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Proposition de contribution — Lire le français`}
              className="group bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-2xl px-5 py-4 flex items-center gap-3 transition-colors"
              data-testid="authors-contact-email"
            >
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold">Contact</p>
                <p className="text-[13px] font-bold truncate">{CONTACT_EMAIL}</p>
              </div>
            </a>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = `mailto:${CONTACT_EMAIL}?subject=Proposition de contribution — Lire le français`;
              }}
              className="group bg-white border border-[#D4AF37] hover:bg-[#FAF7F0] text-[#1A2F4A] rounded-2xl px-5 py-4 flex items-center gap-3 transition-colors text-left"
              data-testid="authors-contact-form"
            >
              <div className="w-10 h-10 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
                <Feather className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold">Formulaire</p>
                <p className="text-[13px] font-bold">Proposer un texte</p>
              </div>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
